import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, ArrowRightLeft, PackageMinus, Undo2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { previewLagerMovement, postLagerMovement, cancelLagerMovement, getLagerMovements, getLagerMovementReasons } from 'core/api/apiService';

/**
 * MovementBlock — Warenbewegungen aus dem Materialdetail (Contract §3.1, Slice 2a; Admin-only).
 *
 * Zwei Stufen, wie beim BANF-Schreibpfad: (1) "Vorschau" = Trockenlauf in SAP (nichts gebucht),
 * zeigt die SAP-Meldungen; (2) "Buchen" ist erst nach einer sauberen Vorschau mit UNVERAENDERTEN
 * Eingaben moeglich und braucht die Bestaetigungs-Checkbox. Kein window.confirm (blockiert die
 * Browser-Automatisierung). Jeder Klick auf "Buchen" traegt eine eigene request_id — ein
 * Doppelklick bucht nie zweimal (Ledger lager_movements, dedup_hash).
 *
 * Props:
 *   data        Object   getLagerMaterialDetail-Ergebnis (stock[] liefert die Lagerorte)
 *   werk        string
 *   user        Object   role==='admin' oder permissions.can_post_goods_movements sieht den Block
 *   showSuccess / showError (msg) => void
 *   onPosted    () => void   nach Buchung/Storno: Detail neu laden
 */
const OPS = ['transfer', 'issue-cost-center', 'scrap'];
const OP_LABEL = { transfer: 'lm.mvOpTransfer', 'issue-cost-center': 'lm.mvOpIssueCostCenter', scrap: 'lm.mvOpScrap' };
const OP_BWART = { transfer: '311', 'issue-cost-center': '201', scrap: '551' };

const newRequestId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const MovementBlock = ({ data, werk, user, showSuccess, showError, onPosted }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const mat = data?.material ?? {};
  const ownRows = (Array.isArray(data?.stock) ? data.stock : []).filter((s) => s.stock_type === 'own');
  // Lagerverwaltete Lagerorte (wm=true, T320) bleiben sichtbar, aber nicht waehlbar: dort erzeugt
  // SAP Lieferungen statt Materialbelege (Nachweis 2026-09-24), das Blatt lehnt sie ab.
  const wmSet = new Set(ownRows.filter((s) => s.wm).map((s) => s.lgort));
  const lgorts = [...new Set(ownRows.map((s) => s.lgort).filter(Boolean))];
  const plainLgorts = lgorts.filter((l) => !wmSet.has(l));

  const [op, setOp] = useState('transfer');
  const [lgort, setLgort] = useState(plainLgorts[0] || '');
  const [lgortTo, setLgortTo] = useState('');
  const [kostl, setKostl] = useState('');
  const [grund, setGrund] = useState('');
  const [reasons, setReasons] = useState({ reason: 'none', rows: [] });
  const [menge, setMenge] = useState('1');
  const [budat, setBudat] = useState('');
  const [text, setText] = useState('');
  const [preview, setPreview] = useState(null);     // letzte Vorschau-Antwort
  const [previewKey, setPreviewKey] = useState('');  // Eingaben, fuer die die Vorschau gilt
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [movements, setMovements] = useState([]);
  const [cancelArmed, setCancelArmed] = useState(null); // id, dessen Storno per Trockenlauf geprueft ist

  // Entscheidung (e), Slice 2b/2: Admin ODER Profilrecht can_post_goods_movements (Backend prueft dasselbe).
  const isAdmin = user?.role === 'admin' || !!user?.permissions?.can_post_goods_movements;
  const formKey = JSON.stringify({ op, lgort, lgortTo, kostl, grund, menge, budat, text });
  const previewValid = !!preview && preview.ok === true && previewKey === formKey;
  const mengeNum = Number(menge);
  const formValid = mat.matnr && werk && lgort && !wmSet.has(lgort) && mengeNum > 0
    && (op === 'transfer' ? (lgortTo && lgortTo.toUpperCase() !== lgort.toUpperCase() && !wmSet.has(lgortTo.toUpperCase())) : !!kostl)
    && !(op === 'scrap' && reasons.reason === 'required' && !grund);

  const body = () => ({
    op, matnr: mat.matnr, werks: werk, lgort, lgort_to: op === 'transfer' ? lgortTo : '', kostl: op !== 'transfer' ? kostl : '',
    grund: op === 'scrap' ? grund : '', menge, meins: mat.meins || '', budat: budat || '', text,
  });

  const loadMovements = useCallback(async () => {
    if (!isAdmin || !mat.matnr) return;
    try {
      const r = await getLagerMovements(user, { werk, matnr: mat.matnr, limit: 10 });
      setMovements(Array.isArray(r?.movements) ? r.movements : []);
    } catch (_) { /* Liste ist Beiwerk */ }
  }, [isAdmin, mat.matnr, werk, user]);

  useEffect(() => { loadMovements(); }, [loadMovements]);
  // Bewegungsgruende nur fuer die Verschrottung laden (T157D); required/optional steuert das Formular.
  useEffect(() => {
    if (!isAdmin || op !== 'scrap') return;
    let alive = true;
    getLagerMovementReasons(user, OP_BWART.scrap)
      .then((r) => { if (alive && r && r.ok) setReasons({ reason: r.reason || 'none', rows: Array.isArray(r.rows) ? r.rows : [] }); })
      .catch(() => { if (alive) setReasons({ reason: 'unknown', rows: [] }); });
    return () => { alive = false; };
  }, [isAdmin, op, user]);
  useEffect(() => { setPreview(null); setConfirmed(false); }, [formKey]);

  if (!isAdmin) return null;

  const errText = (err) => err?.response?.data?.message || err?.message || String(err);
  const messagesOf = (r) => (Array.isArray(r?.messages) ? r.messages : []);

  const handlePreview = async () => {
    setBusy(true);
    try {
      const r = await previewLagerMovement(user, body());
      setPreview(r); setPreviewKey(formKey);
    } catch (err) {
      setPreview({ ok: false, message: errText(err), messages: err?.response?.data?.messages || [] }); setPreviewKey(formKey);
    } finally { setBusy(false); }
  };

  const handlePost = async () => {
    if (!previewValid || !confirmed) return;
    setBusy(true);
    try {
      const r = await postLagerMovement(user, { ...body(), confirm: 'yes-goods-movement', request_id: newRequestId() });
      showSuccess(t('lm.mvPosted', { doc: r.mat_doc, year: r.doc_year }));
      // onPosted laedt das Detail neu (der Block wird dabei neu gemountet und holt die Liste selbst).
      if (onPosted) onPosted(); else { setPreview(null); setConfirmed(false); await loadMovements(); }
    } catch (err) {
      showError(t('lm.mvPostError') + ': ' + errText(err));
    } finally { setBusy(false); }
  };

  // Storno in zwei Schritten wie das Buchen: 1. Klick = Trockenlauf (prueft Beleg/Herkunft in SAP)
  // und scharfstellen, 2. Klick auf denselben Eintrag = echtes Storno. Kein window.confirm.
  const handleCancel = async (m) => {
    setBusy(true);
    try {
      if (cancelArmed !== m.id) {
        await cancelLagerMovement(user, m.id, { testrun: true });
        setCancelArmed(m.id);
        return;
      }
      const r = await cancelLagerMovement(user, m.id, { confirm: 'yes-goods-movement' });
      setCancelArmed(null);
      showSuccess(t('lm.mvCancelled', { doc: m.mat_doc, rev: r.mat_doc }));
      if (onPosted) onPosted(); else await loadMovements();
    } catch (err) {
      setCancelArmed(null);
      showError(t('lm.mvCancelError') + ': ' + errText(err));
    } finally { setBusy(false); }
  };

  const cellMuted = isDarkMode ? 'text-white/50' : 'text-slate-500';
  const cellVal = isDarkMode ? 'text-white/90' : 'text-slate-800';
  const inputCls = `w-full px-2.5 py-1.5 rounded-lg text-sm border ${isDarkMode ? 'bg-white/5 border-white/15 text-white/90' : 'bg-white border-slate-300 text-slate-800'}`;
  const primaryBtn = `flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${isDarkMode ? 'bg-brand/80 hover:bg-brand text-white' : 'bg-brand hover:bg-blue-700 text-white'} disabled:opacity-40 disabled:cursor-not-allowed`;
  const secondaryBtn = `flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${isDarkMode ? 'border-white/20 text-white/70 hover:text-white/90' : 'border-slate-300 text-slate-600 hover:text-brand'} disabled:opacity-40 disabled:cursor-not-allowed`;

  return (
    <div className={`mt-4 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
      <div className="flex items-center gap-2 mb-2">
        <ArrowRightLeft className={`w-4 h-4 ${isDarkMode ? 'text-brand/70' : 'text-brand'}`} />
        <p className={`text-xs font-semibold uppercase tracking-wide ${cellMuted}`}>{t('lm.mvTitle')}</p>
      </div>
      <p className={`text-[11px] mb-2 ${cellMuted}`}>{t('lm.mvHint')}{wmSet.size ? ` ${t('lm.mvWmHint', { list: [...wmSet].join(', ') })}` : ''}</p>

      <div className="flex gap-2 mb-2">
        {OPS.map((o) => (
          <button key={o} type="button" onClick={() => setOp(o)}
            className={`px-3 py-1 rounded-lg text-xs border ${op === o ? (isDarkMode ? 'border-brand bg-brand/20 text-white' : 'border-brand bg-brand/10 text-brand') : (isDarkMode ? 'border-white/15 text-white/60' : 'border-slate-300 text-slate-600')}`}>
            {o === 'transfer' ? <ArrowRightLeft className="inline w-3 h-3 mr-1" /> : <PackageMinus className="inline w-3 h-3 mr-1" />}
            {t(OP_LABEL[o])}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={`block text-[11px] mb-0.5 ${cellMuted}`}>{t('lm.mvFrom')} *</label>
          {lgorts.length ? (
            <select value={lgort} onChange={(e) => setLgort(e.target.value)} className={inputCls}>
              {/* Native Optionen: Chrome malt die Liste weiss, die geerbte helle Schrift waere unsichtbar */}
              {lgorts.map((l) => <option key={l} value={l} disabled={wmSet.has(l)} className="text-slate-900 bg-white">{l}{wmSet.has(l) ? ` ${t('lm.mvWmSuffix')}` : ''}</option>)}
            </select>
          ) : (
            <input value={lgort} onChange={(e) => setLgort(e.target.value.toUpperCase())} className={inputCls} />
          )}
        </div>
        {op === 'transfer' ? (
          <div>
            <label className={`block text-[11px] mb-0.5 ${cellMuted}`}>{t('lm.mvTo')} *</label>
            <input value={lgortTo} onChange={(e) => setLgortTo(e.target.value.toUpperCase())} className={inputCls} placeholder="z. B. 101A" list="mv-lgort-list" />
            <datalist id="mv-lgort-list">{plainLgorts.map((l) => <option key={l} value={l} />)}</datalist>
          </div>
        ) : (
          <div>
            <label className={`block text-[11px] mb-0.5 ${cellMuted}`}>{t('lm.mvKostl')} *</label>
            <input value={kostl} onChange={(e) => setKostl(e.target.value)} className={inputCls} />
          </div>
        )}
        {op === 'scrap' && (
          <div className="col-span-2">
            <label className={`block text-[11px] mb-0.5 ${cellMuted}`}>{t('lm.mvGrund')}{reasons.reason === 'required' ? ' *' : ''}</label>
            {reasons.rows.length ? (
              <select value={grund} onChange={(e) => setGrund(e.target.value)} className={inputCls}>
                <option value="" className="text-slate-900 bg-white">{reasons.reason === 'required' ? t('lm.mvGrundChoose') : t('lm.mvGrundNone')}</option>
                {reasons.rows.map((r) => <option key={r.code} value={r.code} className="text-slate-900 bg-white">{r.code} – {r.text}</option>)}
              </select>
            ) : (
              <input value={grund} maxLength={4} onChange={(e) => setGrund(e.target.value)} className={inputCls} placeholder={t('lm.mvGrundFree')} />
            )}
          </div>
        )}
        <div>
          <label className={`block text-[11px] mb-0.5 ${cellMuted}`}>{t('lm.mvMenge')} {mat.meins ? <span className="font-mono">{mat.meins}</span> : null} *</label>
          <input type="number" min="0.001" step="1" value={menge} onChange={(e) => setMenge(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={`block text-[11px] mb-0.5 ${cellMuted}`}>{t('lm.mvBudat')}</label>
          <input type="date" value={budat} onChange={(e) => setBudat(e.target.value)} className={inputCls} />
        </div>
        <div className="col-span-2">
          <label className={`block text-[11px] mb-0.5 ${cellMuted}`}>{t('lm.mvText')}</label>
          <input value={text} maxLength={50} onChange={(e) => setText(e.target.value)} className={inputCls} />
        </div>
      </div>

      {preview && (
        <div className={`mt-2 px-2.5 py-2 rounded-lg text-xs break-words ${preview.ok ? (isDarkMode ? 'bg-emerald-500/10 text-emerald-200' : 'bg-emerald-50 text-emerald-800') : (isDarkMode ? 'bg-red-500/10 text-red-200' : 'bg-red-50 text-red-800')}`}>
          <div className="flex items-center gap-1.5 font-semibold">
            {preview.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
            {preview.ok ? t('lm.mvPreviewOk', { bwart: preview.bwart || '' }) : t('lm.mvPreviewFailed')}
          </div>
          {preview.message && <p className="mt-0.5">{preview.message}</p>}
          {messagesOf(preview).map((m, i) => <p key={i} className="mt-0.5 font-mono">{m.type} {m.id} {m.number}: {m.message}</p>)}
        </div>
      )}

      <div className="flex items-center gap-2 mt-2">
        <button type="button" onClick={handlePreview} disabled={busy || !formValid} className={secondaryBtn}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{t('lm.mvPreview')}
        </button>
        <label className={`flex items-center gap-1.5 text-[11px] ${cellMuted} ${previewValid ? '' : 'opacity-40'}`}>
          <input type="checkbox" checked={confirmed} disabled={!previewValid} onChange={(e) => setConfirmed(e.target.checked)} />
          {t('lm.mvConfirmLabel')}
        </label>
        <button type="button" onClick={handlePost} disabled={busy || !previewValid || !confirmed} className={primaryBtn}>
          {t('lm.mvPost')}
        </button>
      </div>

      {movements.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <p className={`text-[11px] font-semibold uppercase tracking-wide mb-1 ${cellMuted}`}>{t('lm.mvRecent')}</p>
          <table className="w-full text-xs">
            <tbody>
              {movements.map((m) => (
                <tr key={m.id} className={`border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                  <td className={`py-1 pr-2 ${cellMuted}`}>{m.created_at ? new Date(m.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</td>
                  <td className={`py-1 pr-2 ${cellVal}`}>{m.bwart} {m.lgort}{m.lgort_to ? ` → ${m.lgort_to}` : ''}{m.kostl ? ` → ${m.kostl}` : ''}</td>
                  <td className={`py-1 pr-2 text-right ${cellVal}`}>{Number(m.menge)} {m.meins}</td>
                  <td className={`py-1 pr-2 font-mono ${cellMuted}`}>{m.mat_doc}{m.cancel_doc ? ` ⟲ ${m.cancel_doc}` : ''}</td>
                  <td className={`py-1 ${cellMuted}`}>{m.status}</td>
                  <td className="py-1 text-right">
                    {m.status === 'posted' && (
                      <span className="inline-flex items-center gap-1">
                        <button type="button" onClick={() => handleCancel(m)} disabled={busy} title={t(cancelArmed === m.id ? 'lm.mvCancelConfirm' : 'lm.mvCancel')}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] border ${cancelArmed === m.id ? (isDarkMode ? 'border-red-400 text-red-200 bg-red-500/10' : 'border-red-500 text-red-700 bg-red-50') : (isDarkMode ? 'border-white/20 text-white/70' : 'border-slate-300 text-slate-600')} disabled:opacity-40`}>
                          <Undo2 className="w-3 h-3" />{t(cancelArmed === m.id ? 'lm.mvCancelConfirm' : 'lm.mvCancel')}
                        </button>
                        {cancelArmed === m.id && (
                          <button type="button" onClick={() => setCancelArmed(null)} disabled={busy} className={`text-[11px] underline ${cellMuted}`}>{t('lm.mvCancelAbort')}</button>
                        )}
                      </span>
                    )}
                    {m.status === 'unknown' && <span className={`text-[11px] ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{t('lm.mvUnknown')}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MovementBlock;
